// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// API
import { deleteProgressionAnalysis } from "../../api";

export const useDeleteProgressionAnalyses = (
  clinicId: string | undefined,
  patientId: string
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (analysisIds: Array<string>) => {
      if (!clinicId || !patientId) throw new Error();

      try {
        const token = await getToken();
        return deleteProgressionAnalysis(
          token,
          clinicId,
          patientId,
          analysisIds
        );
      } catch (error) {
        throw error;
      }
    },
    // Invalidate analyses query cache
    onSuccess: (_, analysisIds) => {
      queryClient.invalidateQueries({
        queryKey: ["progression-analyses", clinicId, patientId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Progression analysis deleted successfully!", {
          count: analysisIds.length,
        }),
      });
    },
    onError: (_, analysisIds) => {
      setSnackbarInfo({
        variant: "error",
        message: t("Progression analysis could not be deleted!", {
          count: analysisIds.length,
        }),
      });
    },
  });
};
